import { useEffect, useMemo, useState } from 'react'
import { Link, useParams } from 'react-router-dom'

const PLACEHOLDER_AVATAR = '/teacher-placeholder.svg'

interface LockerInviteProps {
  code?: string;
}

interface LockerInviteState {
  status: 'loading' | 'ready' | 'error';
  data: Record<string, unknown> | null;
  error: string;
}

export default function LockerInvite({ code: providedCode }: LockerInviteProps) {
  const { code: routeCode } = useParams()
  const code = providedCode || routeCode
  const [state, setState] = useState<LockerInviteState>({ status: 'loading', data: null, error: '' })

  useEffect(() => {
    let cancelled = false

    const load = async () => {
      if (!code) {
        setState({ status: 'error', data: null, error: 'Share link not found' })
        return
      }

      setState({ status: 'loading', data: null, error: '' })

      try {
        const base = typeof window !== 'undefined' ? window.location.origin : 'https://projecttower.org'
        const url = new URL('/api/v1/locker-room/invite', base)
        url.searchParams.set('code', code)

        const response = await fetch(url.toString())
        const payload = await response.json().catch(() => ({}))

        if (!response.ok) {
          const errMsg = typeof payload?.error === 'string' ? payload.error : payload?.error?.message || 'invalid_or_expired'
          throw new Error(errMsg)
        }

        if (!cancelled) {
          setState({ status: 'ready', data: payload?.data || payload, error: '' })
        }
      } catch (error: unknown) {
        if (!cancelled) {
          const errorMessage = error instanceof Error ? error.message : String(error);
          const friendlyMessage =
            errorMessage === 'invalid_or_expired' || errorMessage === 'not_found'
              ? 'Link is invalid or expired.'
              : errorMessage || 'Link is invalid or expired.'
          setState({ status: 'error', data: null, error: friendlyMessage })
        }
      }
    }

    load()

    return () => {
      cancelled = true
    }
  }, [code])

  const avatarSrc = useMemo(() => {
    const prospect = state.data?.prospect as Record<string, unknown> | undefined;
    const avatarUrl = prospect?.avatarUrl as string | undefined;
    return avatarUrl || PLACEHOLDER_AVATAR
  }, [state.data])

  if (state.status === 'loading') {
    return (
      <div className="min-h-screen bg-shell/60 p-6">
        <div className="mx-auto max-w-2xl rounded-3xl bg-white/80 p-8 text-center text-cocoa/70 shadow-soft">
          Loading locker room…
        </div>
      </div>
    )
  }

  if (state.status === 'error' || !state.data) {
    return (
      <div className="min-h-screen bg-shell/60 p-6">
        <div className="mx-auto max-w-2xl rounded-3xl bg-white/80 p-8 text-center text-cocoa/70 shadow-soft">
          {state.error || 'Link is invalid or has expired.'}
        </div>
      </div>
    )
  }

  const prospect = state.data.prospect as Record<string, unknown> | undefined;
  const lockerRoom = state.data.lockerRoom as Record<string, unknown> | undefined;
  const createdAt = lockerRoom?.createdAt as string | undefined;

  return (
    <div className="min-h-screen bg-shell p-6 text-cocoa">
      <div className="mx-auto max-w-3xl space-y-6 rounded-3xl bg-white p-6 shadow-soft">
        <header className="flex flex-col gap-4 sm:flex-row sm:items-center">
          <img
            src={avatarSrc}
            alt={(prospect as Record<string, unknown>)?.displayName as string || 'Athlete avatar'}
            className="h-24 w-24 rounded-full border border-sand object-cover"
            onError={(event: React.SyntheticEvent<HTMLImageElement>) => {
              if (event.currentTarget.src !== PLACEHOLDER_AVATAR) {
                event.currentTarget.src = PLACEHOLDER_AVATAR
              }
            }}
          />
          <div className="space-y-1">
            <p className="text-xs uppercase tracking-wide text-cocoa/60">Locker Room Invite</p>
            <h1 className="text-3xl font-semibold text-cocoa">Welcome to the locker room</h1>
            {prospect?.displayName ? (
              <p className="text-lg font-semibold text-cocoa/90">{prospect.displayName as string}</p>
            ) : null}
            {prospect?.headline ? <p className="text-sm text-cocoa/70">{prospect.headline as string}</p> : null}
          </div>
        </header>

        <div className="space-y-3 rounded-2xl border border-sand bg-shell/30 p-4">
          <p className="text-base leading-relaxed text-cocoa/85">
            This link shares a snapshot of the athlete&apos;s locker room. Keep it handy if you need to revisit or share it with a
            trusted adult.
          </p>
          {createdAt ? (
            <p className="text-xs uppercase tracking-wide text-cocoa/60">
              Link created {new Date(createdAt).toLocaleString()}
            </p>
          ) : null}
        </div>

        <div className="flex flex-col gap-3 sm:flex-row">
          <button
            type="button"
            className="btn flex-1"
            onClick={() => {
              const inviteLink = window.location.href
              try {
                navigator.clipboard?.writeText?.(inviteLink)
              } catch (err) {
                window.prompt('Copy this locker room link', inviteLink)
              }
            }}
          >
            Copy invite link
          </button>
          <Link to="/contact" className="btn flex-1 bg-white text-cocoa hover:bg-shell/50">
            Need help?
          </Link>
        </div>

        <div className="rounded-xl border border-sand bg-shell/30 p-4 text-xs text-cocoa/70">
          Share code: <span className="font-mono text-cocoa/90">{code}</span>
        </div>
      </div>
    </div>
  )
}
